// ─────────────────────────────────────────────────────────────────────────────
// What kind of cost is a budget category?
//
// The answer is *derived*, never stored: a category's class is decided entirely
// by what in Setup points at it. That makes an inconsistent state
// unrepresentable — there is no flag to fall out of sync with the plan, and no
// way for the same envelope to be counted in two arms of the cut.
//
// Exactly one owner, in this precedence order:
//
//   savings plan  → SAVINGS   cuts hit it via the savings share
//   debt plan     → DEBT      cuts hit it via the debt share
//   spending goal → VARIABLE  cuts hit it via the variable share
//   nothing       → FIXED     never cut
//
// A category owned by nothing is Fixed by definition. That is deliberate: money
// planned against it still has to appear in the strategy total, and "fixed" is
// the answer that never cuts something the user didn't ask to have cut.
// ─────────────────────────────────────────────────────────────────────────────

export const CLASS = {
  FIXED:    'fixed',
  VARIABLE: 'variable',
  SAVINGS:  'savings',
  DEBT:     'debt',
};

export const CLASS_LABEL = {
  [CLASS.FIXED]:    'Fixed',
  [CLASS.VARIABLE]: 'Variable',
  [CLASS.SAVINGS]:  'Savings',
  [CLASS.DEBT]:     'Debt',
};

/** Token names only — the caller decides how to paint them. */
export const CLASS_COLOR = {
  [CLASS.FIXED]:    { fg: 'var(--text3)',  bg: 'transparent',    border: 'var(--border2)' },
  [CLASS.VARIABLE]: { fg: 'var(--amber)',  bg: 'var(--amber-bg)',  border: 'var(--amber)'  },
  [CLASS.SAVINGS]:  { fg: 'var(--blue)',   bg: 'var(--blue-bg)',   border: 'var(--blue)'   },
  [CLASS.DEBT]:     { fg: 'var(--purple)', bg: 'var(--purple-bg)', border: 'var(--purple)' },
};

const byLinkedName = (rows) => {
  const map = new Map();
  for (const r of rows || []) {
    const n = r?.linkedCategoryName;
    if (n) map.set(n, r);           // first link wins if two point at one category
  }
  return map;
};

/**
 * Classify every category name against the household's Setup model.
 *
 * @returns Map<categoryName, {
 *   cls, label, owner,        // owner = the plan/goal name, or null when Fixed
 *   ownerKind,                // 'spending goal' | 'savings plan' | 'debt plan' | null
 *   conflict                  // true when more than one thing in Setup claims it
 * }>
 */
export function classifyCategories({ categories = [], spendingGoals = [], savingsPlans = [], debtPlans = [] }) {
  const goals   = byLinkedName(spendingGoals);
  const savings = byLinkedName(savingsPlans);
  const debts   = byLinkedName(debtPlans);

  const out = new Map();
  for (const c of categories) {
    const name = c.name;
    const claims = [
      savings.has(name) && { cls: CLASS.SAVINGS,  kind: 'savings plan',  owner: savings.get(name) },
      debts.has(name)   && { cls: CLASS.DEBT,     kind: 'debt plan',     owner: debts.get(name)   },
      goals.has(name)   && { cls: CLASS.VARIABLE, kind: 'spending goal', owner: goals.get(name)   },
    ].filter(Boolean);

    const won = claims[0];
    out.set(name, won
      ? {
          cls: won.cls, label: CLASS_LABEL[won.cls],
          owner: won.owner?.name || null, ownerKind: won.kind,
          conflict: claims.length > 1,
        }
      : { cls: CLASS.FIXED, label: CLASS_LABEL[CLASS.FIXED], owner: null, ownerKind: null, conflict: false });
  }
  return out;
}

/** Every category name Setup claims — i.e. everything that is NOT a fixed cost. */
export function ownedCategoryNames({ spendingGoals = [], savingsPlans = [], debtPlans = [] }) {
  return new Set([
    ...spendingGoals.map(g => g.linkedCategoryName),
    ...savingsPlans.map(p => p.linkedCategoryName),
    ...debtPlans.map(p => p.linkedCategoryName),
  ].filter(Boolean));
}

/**
 * Human explanation for the badge tooltip. Works with a full classification or
 * with just a class name, so callers that only have the server's `costClass`
 * (the Trends tab) can use it too.
 */
export function describeClass(info) {
  if (!info) return '';
  const by = (kind) => info.owner ? `driven by the "${info.owner}" ${kind} in Setup` : `driven by a ${kind} in Setup`;
  switch (info.cls) {
    case CLASS.VARIABLE:
      return `Variable — ${by('spending goal')}. `
           + 'Budget cuts reduce it first (the largest share of any shortfall).';
    case CLASS.SAVINGS:
      return `Savings — ${by('savings plan')}. `
           + 'Cuts reach it only after variable spending and debt extras.';
    case CLASS.DEBT:
      return `Debt — ${by('debt plan')}. `
           + 'The base payment is never cut; only the payoff extra is.';
    default:
      return 'Fixed — nothing in Setup drives this, so budget cuts never touch it. '
           + 'Link a spending goal to it in Setup to make it cuttable.';
  }
}
